import {
  Alert,
  Box,
  Button,
  ColumnLayout,
  FormField,
  Header,
  Input,
  Modal,
  Multiselect,
  Select,
  SpaceBetween,
  Table,
} from '@amzn/awsui-components-react-v3';
import { Link } from 'src/components/common/Link';
import React, { useEffect, useState } from 'react';
import { getGroupInfo, getTaggedResources, listDataZoneEntities } from 'src/api/permissions';
import { useCollection } from '@amzn/awsui-collection-hooks';
import { isValidPrincipalNotRoot, isValidWorkspace } from 'src/commons/validationUtils';
import { getConsumerDataLakePrincipal, getFoundryRoleARN } from 'src/commons/common';
import {
  DATABASE_RESOURCE_TYPE,
  DATASET_RESOURCE_TYPE,
  DATAZONE_TYPE,
  DZ_GLUE_DATASOURCE_ID,
  DZ_REDSHIFT_DATASOURCE_ID,
  FGA_POLICY_TYPE_PREFIX,
  GALAXI_DATASOURCE_ID,
  GALAXI_NEW_DATA_ACCESS_CATEGORY,
  GALAXI_NEW_DATA_ACCESS_ITEM,
  GALAXI_NEW_DATA_ACCESS_TYPE,
  GALAXI_TYPE,
  LAKE_FORMATION_DATASOURCE_ID,
  LAKE_FORMATION_TAG_ITEM,
  LAKEFORMATION_RESOURCE,
  LAKEFORMATION_TAG_TYPE,
  REDSHIFT_RESOURCE,
  REDSHIFT_TAG_ITEM,
  REDSHIFT_TAG_TYPE,
  RESOURCE_ITEM,
  SCHEMA_RESOURCE_TYPE,
  TAG_ITEM,
  WEBSITE_NAME,
} from 'src/commons/constants';
import {
  createDatabaseDetailLink,
  createDatasetDetailLink,
  createFgaPolicyViewLink,
  createSchemaDetailLink,
} from 'src/routes';
import { constructTicketUrlWithParams, getGalaxiSubscriptionURL } from 'src/components/workspaces/common/common';
import { GalaxiGroup, GetGroupResult } from 'aws-sdk/clients/awsdatalakegladstonelambda';
import { EmptyState } from 'src/commons/EmptyState';
import { SelectProps } from '@amzn/awsui-components-react-v3/polaris/select/interfaces';
import { isDWaaSGroup } from 'src/api/config';
import { CartAccessItem } from 'src/components/common/checkout/common';

export interface AccessSelectModalProps {
  setContentType: any;
  activeGroup: string;
  username: string;
  activeWorkspace?: any;
  visible: boolean;
  dismiss: any;
  dataset: any;
  addToCart: (items: CartAccessItem[]) => void;
  catalogName: any;
  cartItemIds: any;
  cartItems?: any;
  galaxiGroups?: GalaxiGroup[];
  allowListed?: boolean;
  selectedPermissionType?: any;
  allPermissionDropdownOptions: any[];
}

export const AccessSelectModal = (props: AccessSelectModalProps) => {
  const [selectedPermission, setSelectedPermission] = useState(undefined);
  const [selectedPermissions, setSelectedPermissions] = useState(undefined);
  const [allItems, setItems] = useState([]);
  const [selectedPrincipalType, setSelectedPrincipalType] = useState(undefined);
  const [principal, setPrincipal] = useState(undefined);
  const [principalErrorText, setPrincipalErrorText] = useState(undefined);
  const [galaxiGroupErrorText, setGalaxiGroupErrorText] = useState(undefined);
  const [dropdownPrincipalOptions, setDropDownPrincipalOptions] = useState([]);
  const [loadingResources, setLoadingResources] = useState(false);
  const [dzProjectOptions, setDzProjectOptions] = useState<SelectProps.Option[]>([]);
  const [selectedDzProject, setSelectedDzProject] = useState<SelectProps.Option>(undefined);
  const [selectedGalaxiGroup, setSelectedGalaxiGroup] = useState(undefined);

  const groupServiceSupported = props.galaxiGroups != null;
  const onboardedToGalaxi = props.galaxiGroups?.length > 0;
  const isGalaxiPermission = props.selectedPermissionType == GALAXI_TYPE;
  const isRedshift = props.dataset?.ClusterIdentifier || props.dataset?.RedshiftWorkgroupName;
  const isDataZone =
    props.dataset?.DataSourceType == DZ_REDSHIFT_DATASOURCE_ID ||
    props.dataset?.DataSourceType == DZ_GLUE_DATASOURCE_ID;

  let buttonText = 'Add to cart';
  if (props.dataset?.IdInfo.DataSourceId == GALAXI_DATASOURCE_ID) {
    if (groupServiceSupported && !onboardedToGalaxi) {
      buttonText = 'Onboard to Galaxi';
    } else {
      buttonText = 'Request access';
    }
  } else if (isGalaxiPermission) {
    buttonText = 'Cut access ticket to Galaxi support team';
  }

  const flattenDatasetAccess = (selectedPermission): CartAccessItem => {
    return {
      datasetName: selectedPermission.datasetName,
      databaseName: selectedPermission.databaseName,
      tableName: selectedPermission.datasetName,
      catalogId: selectedPermission.catalogId,
      region: props.dataset?.IdInfo?.Region,
      catalogDisplayName: selectedPermission.catalogName,
      dataOwnerRole: isValidWorkspace(selectedPermission.owner)
        ? getFoundryRoleARN(props.dataset?.IdInfo?.Region, selectedPermission.catalogId)
        : selectedPermission.publisherRole,
      owner: selectedPermission.owner,
      itemType: RESOURCE_ITEM,
      permissionId: selectedPermission.permissionId,
      catalogName: selectedPermission.catalogName,
      id: selectedPermission.id,
      dataLakePrincipal: principal === undefined ? props.activeWorkspace.accountId : principal,
      // DZ fields
      dataZoneAssetId: selectedPermission.dataZoneAssetId,
      dataZoneConsumerProjectId: selectedDzProject?.value,
      clusterId: selectedPermission.clusterId,
      redshiftWorkgroupName: selectedPermission.redshiftWorkgroupName,
      schemaName: selectedPermission.schemaName,
    };
  };

  let flattenTagAccess = async (selectedPermission): Promise<CartAccessItem> => {
    let catalogId = selectedPermission.catalogId;
    let catalogName = selectedPermission.catalogName;
    let permissionId = selectedPermission.permissionId;
    let tagKey = selectedPermission.tagKey;
    let tagValue = selectedPermission.tagValue;
    let owner = selectedPermission.owner;
    let id = selectedPermission.id;
    let resources = [];
    let getTaggedResourcesResponse = await getTaggedResources({
      tagPair: tagKey + ':' + tagValue,
      catalogId: selectedPermission.catalogId,
      region: props.dataset?.IdInfo?.Region,
      type: props.dataset?.ClusterIdentifier != null ? REDSHIFT_TAG_TYPE : LAKEFORMATION_TAG_TYPE,
    });

    for (let resource of getTaggedResourcesResponse.resources) {
      if (resource['resourceType'] == DATASET_RESOURCE_TYPE) {
        if (resource['schemaName'] != null) {
          resources.push(
            `${catalogName}.${resource['databaseName']}.${resource['schemaName']}.${resource['resourceName']}`,
          );
        } else {
          resources.push(`${catalogName}/${resource['databaseName']}/${resource['resourceName']}`);
        }
      } else if (resource['resourceType'] == DATABASE_RESOURCE_TYPE) {
        resources.push(`${catalogName}/${resource['databaseName']}`);
      } else if (resource['resourceType'] == SCHEMA_RESOURCE_TYPE) {
        resources.push(`${catalogName}.${resource['databaseName']}.${resource['databaseName']}`);
      }
    }

    return {
      tagKey: tagKey,
      tagValue: tagValue,
      tag: tagKey + '.' + tagValue,
      owner: owner,
      permissionId: permissionId,
      itemType: props.dataset?.ClusterIdentifier != null ? REDSHIFT_TAG_ITEM : LAKE_FORMATION_TAG_ITEM,
      resources: resources,
      catalogDisplayName: catalogName,
      dataOwnerRole: getFoundryRoleARN(props.dataset?.IdInfo?.Region, catalogId),
      catalogId: catalogId,
      catalogName: catalogName,
      datasetName: undefined,
      databaseName: undefined,
      tableName: undefined,
      id: id,
      region: props.dataset?.IdInfo?.Region,
      dataLakePrincipal:
        props.dataset?.ClusterIdentifier != null
          ? getConsumerDataLakePrincipal(REDSHIFT_RESOURCE, undefined, props.activeWorkspace.accountId)
          : getConsumerDataLakePrincipal(LAKEFORMATION_RESOURCE, principal, props.activeWorkspace.accountId),
    };
  };

  let flattenFineGrainAccess = (selectedPermission): CartAccessItem => {
    return {
      catalogDisplayName: selectedPermission.catalogName,
      catalogName: selectedPermission.catalogName,
      catalogId: selectedPermission.catalogId,
      clusterId: selectedPermission.clusterId,
      databaseName: selectedPermission.databaseName,
      schemaName: selectedPermission.schemaName,
      tableName: selectedPermission.tableName,
      datasetName: selectedPermission.datasetName ?? selectedPermission.tableName,
      region: props.dataset?.IdInfo?.Region,
      dataOwnerRole: getFoundryRoleARN(props.dataset?.IdInfo?.Region, selectedPermission.catalogId),
      owner: selectedPermission.ownerId,
      itemType: FGA_POLICY_TYPE_PREFIX,
      permissionId: selectedPermission.permissionId,
      fgapName: selectedPermission.fgapName,
      id: selectedPermission.id,
      dataLakePrincipal:
        props.dataset?.ClusterIdentifier != null
          ? getConsumerDataLakePrincipal(REDSHIFT_RESOURCE, undefined, props.activeWorkspace.accountId)
          : getConsumerDataLakePrincipal(LAKEFORMATION_RESOURCE, principal, props.activeWorkspace.accountId),
    };
  };

  const handleConfirm = async () => {
    if (isGalaxiPermission && props.dataset?.IdInfo.DataSourceId == GALAXI_DATASOURCE_ID) {
      if (groupServiceSupported && !onboardedToGalaxi) {
        window.open('https://w.amazon.com/bin/view/AWS/WWRO/BigDataPlatform/GalaxiAccessManager/Onboarding/', '_blank');
      } else if (selectedPermission) {
        //selected group is selectedPermission?.value
        //need to append it to url
        window.open(
          getGalaxiSubscriptionURL(
            props.dataset?.IdInfo.CatalogId,
            props.dataset?.IdInfo.DatabaseName,
            props.dataset?.IdInfo.TableName,
          ),
          '_blank',
        );
      } else {
        window.open(
          getGalaxiSubscriptionURL(
            props.dataset?.IdInfo.CatalogId,
            props.dataset?.IdInfo.DatabaseName,
            props.dataset?.IdInfo.TableName,
          ),
          '_blank',
        );
      }
      return;
    }

    if (isGalaxiPermission && props.dataset?.IdInfo.DataSourceId != GALAXI_DATASOURCE_ID) {
      let activeGroupInfo: GetGroupResult;
      if (props.activeGroup) {
        activeGroupInfo = await getGroupInfo({
          groupId: props.activeGroup,
        });
      }
      const ticketUrl: URL = constructTicketUrlWithParams({
        category: GALAXI_NEW_DATA_ACCESS_CATEGORY,
        type: GALAXI_NEW_DATA_ACCESS_TYPE,
        item: GALAXI_NEW_DATA_ACCESS_ITEM,
        datasetName: props.dataset?.IdInfo.TableName ?? '',
        databaseName: props.dataset.IdInfo.DatabaseName ?? '',
        requesterTeamName: selectedPermission?.teamName ?? activeGroupInfo?.teamInfo?.Name ?? '',
        requesterGAMGroupName: selectedPermission?.value ?? '',
        requesterEmail: activeGroupInfo?.teamInfo?.DistributionList ?? '',
      });
      window.open(ticketUrl);

      return;
    }

    if (
      !isRedshift &&
      ((selectedPermission == undefined && selectedPermissions == undefined) ||
        (selectedPrincipalType.value === 'iamRoleUserAccess' && !validateIAMPrincipal()) ||
        (isDWaaSGroup(props.activeGroup) && !isGroupSelectedBelongToGalaxi()))
    ) {
      return;
    }

    if (selectedPermissions != undefined && selectedPermission == undefined) {
      let cartItems = [];
      for (let permission of selectedPermissions) {
        if (permission.type == FGA_POLICY_TYPE_PREFIX) {
          let cartItem = await flattenFineGrainAccess(permission);
          cartItems.push(cartItem);
        }
      }
      props.addToCart(cartItems);
      props.dismiss();
      return;
    }

    if (selectedPermission.type == RESOURCE_ITEM || selectedPermission.type == DATAZONE_TYPE) {
      let ds = await flattenDatasetAccess(selectedPermission);
      props.addToCart([ds]);
    } else if (selectedPermission.type == TAG_ITEM) {
      let cartItem = await flattenTagAccess(selectedPermission);
      props.addToCart([cartItem]);
    } else if (selectedPermission.type == FGA_POLICY_TYPE_PREFIX) {
      let cartItem = await flattenFineGrainAccess(selectedPermission);
      props.addToCart([cartItem]);
    }
    props.dismiss();
  };

  const transferResources = async (selectedPermissions: []) => {
    setLoadingResources(true);
    let res = [];

    if (selectedPermissions == undefined) {
      return res;
    }

    for (let selectedPermission of selectedPermissions) {
      if (selectedPermission['type'] == FGA_POLICY_TYPE_PREFIX) {
        res.push({
          type: FGA_POLICY_TYPE_PREFIX,
          resource: `${selectedPermission['fgapName']}`,
          resourceLink: createFgaPolicyViewLink(selectedPermission['id']),
        });
      }
    }
    setItems(res);
    setLoadingResources(false);
  };

  const transferResource = async (selectedPermission) => {
    setLoadingResources(true);
    let res = [];

    if (selectedPermission == undefined) {
      return res;
    }

    //named resource
    if (selectedPermission?.type == RESOURCE_ITEM) {
      res.push({
        type: DATASET_RESOURCE_TYPE,
        resource: `${selectedPermission.catalogName}/${selectedPermission.databaseName}/${selectedPermission.datasetName}`,
        resourceLink: createDatasetDetailLink(props.dataset?.Id),
      });
      setItems(res);
    } else if (selectedPermission.type == TAG_ITEM) {
      //tag resource
      let getTaggedResourcesResponse = await getTaggedResources({
        tagPair: selectedPermission.tagKey + ':' + selectedPermission.tagValue,
        catalogId: selectedPermission.catalogId,
        region: props.dataset?.IdInfo?.Region,
        type: props.dataset.ClusterIdentifier != null ? REDSHIFT_TAG_TYPE : LAKEFORMATION_TAG_TYPE,
      });
      for (let resource of getTaggedResourcesResponse.resources) {
        if (resource.clusterId != null) {
          if (resource.resourceType == DATASET_RESOURCE_TYPE) {
            res.push({
              type: resource.resourceType,
              resource: `${selectedPermission.catalogName}.${resource.clusterId}.${resource.databaseName}.${resource.schemaName}.${resource.resourceName}`,
              resourceLink: createDatasetDetailLink(resource.resourceId),
            });
          } else if (resource.resourceType == SCHEMA_RESOURCE_TYPE) {
            res.push({
              type: resource.resourceType,
              resource: `${selectedPermission.catalogName}.${resource.clusterId}.${resource.databaseName}.${resource.resourceName}`,
              resourceLink: createSchemaDetailLink(
                resource.dataSourceType,
                resource.catalogId,
                resource.clusterId,
                undefined,
                resource.databaseName,
                resource.resourceName,
                resource.region,
              ),
            });
          }
        } else {
          if (resource.resourceType == DATASET_RESOURCE_TYPE) {
            res.push({
              type: resource.resourceType,
              resource: `${selectedPermission.catalogName}/${resource.databaseName}/${resource.resourceName}`,
              resourceLink: createDatasetDetailLink(resource.resourceId),
            });
          } else if (resource.resourceType == DATABASE_RESOURCE_TYPE) {
            res.push({
              type: resource.resourceType,
              resource: `${selectedPermission.catalogName}/${resource.databaseName}`,
              resourceLink: createDatabaseDetailLink(
                LAKE_FORMATION_DATASOURCE_ID,
                resource.catalogId,
                undefined,
                undefined,
                resource.databaseName,
                resource.region,
              ),
            });
          }
        }
      }
      setItems(res);
    } else if (selectedPermission?.type == DATAZONE_TYPE) {
      const listDataZoneEntitiesResult = await listDataZoneEntities({
        groupId: props.activeGroup,
        entityType: 'Project',
      });
      setDzProjectOptions(
        listDataZoneEntitiesResult.entities.map((entity) => ({
          label: entity.groupId,
          value: entity.projectId,
        })),
      );
    }
    setLoadingResources(false);
  };

  const { items } = useCollection(allItems, {
    filtering: {
      empty: <EmptyState title='no resources attached' subtitle='No resources to display' />,
    },
    selection: {},
    propertyFiltering: {
      filteringProperties: [],
    },
  });

  const validateIAMPrincipal = () => {
    let errorText = !isValidPrincipalNotRoot(principal)
      ? 'Invalid IAM principal ARN'
      : // the selected permissions can be a single permission or an array (i.e. FGA policies)
      [selectedPermission, ...(selectedPermissions ?? [])].some((p) => p?.principals?.includes(principal))
      ? 'Principal already consumed or is in your cart'
      : undefined;
    setPrincipalErrorText(errorText);
    return errorText === undefined;
  };

  const isGroupSelectedBelongToGalaxi = () => {
    let errorText = undefined;
    if (isDWaaSGroup(props.activeGroup)) {
      if (selectedPrincipalType.value != 'iamRoleUserAccess') {
        errorText = 'You must choose a Galaxi group to continue with your subscription';
      }
    }
    setGalaxiGroupErrorText(errorText);
    return errorText === undefined;
  };

  const displayResource = () => {
    return (
      <Table
        loading={loadingResources}
        columnDefinitions={[
          {
            id: 'resource',
            header: 'Resource',
            cell: (item) => <Link href={item.resourceLink}>{item.resource}</Link>,
          },
          {
            id: 'type',
            header: 'Resource type',
            cell: (item) => item.type,
          },
        ]}
        items={items}
        loadingText='Loading resources'
        sortingDisabled
        header={<Header> Resources </Header>}
      />
    );
  };

  const handleChange = (e) => {
    if (props.selectedPermissionType == FGA_POLICY_TYPE_PREFIX) {
      setSelectedPermissions(e.detail.selectedOptions);
      setSelectedPermission(undefined);
      setSelectedGalaxiGroup(undefined);
      const options: SelectProps.Option[] = [
        {
          label: 'Workspace access (Entire account)',
          value: 'workspaceAccess',
          disabled: e.detail.selectedOptions.filter((option) => option.workspaceAccess).length > 0,
        },
        {
          label: 'IAM role/user access',
          value: 'iamRoleUserAccess',
        },
      ];
      setDropDownPrincipalOptions(options);
      setSelectedPrincipalType(options[0]);
      transferResources(e.detail.selectedOptions);
    } else if (isGalaxiPermission) {
      setSelectedPermissions(e.detail.selectedOptions);
      setSelectedPermission(e.detail.selectedOption);
      setSelectedPrincipalType(undefined);
    } else {
      setSelectedPermission(e.detail.selectedOption);
      setSelectedPermissions(undefined);
      setSelectedGalaxiGroup(undefined);
      const options: SelectProps.Option[] = [
        {
          label: 'Workspace access (Entire account)',
          value: 'workspaceAccess',
          disabled: e.detail.selectedOption.workspaceAccess,
        },
        {
          label: 'IAM role/user access',
          value: 'iamRoleUserAccess',
        },
      ];
      setDropDownPrincipalOptions(options);
      setSelectedPrincipalType(options[0]);
      transferResource(e.detail.selectedOption);
    }
  };

  const getGalaxiGroupDropdown = () => {
    return (
      <FormField
        label='Target galaxi group'
        description='Select the target galaxi group for your subscription request'
        errorText={galaxiGroupErrorText}
      >
        <SpaceBetween size='m'>
          <Select
            selectedOption={selectedGalaxiGroup}
            onChange={(e) => {
              setGalaxiGroupErrorText(undefined);
              setSelectedGalaxiGroup(e.detail.selectedOption);
              setSelectedPrincipalType({
                label: 'IAM role/user access',
                value: 'iamRoleUserAccess',
              });
              setPrincipal(e.detail.selectedOption['iamRole']);
            }}
            options={props.allPermissionDropdownOptions?.filter((dropdownOption) => dropdownOption.type == GALAXI_TYPE)}
            selectedAriaLabel='Selected'
            statusType={loadingResources ? 'loading' : 'finished'}
            loadingText='Loading access options'
          />
        </SpaceBetween>
      </FormField>
    );
  };

  const getDropDownItemsBasedOnSelectionType = () => {
    if (props.selectedPermissionType) {
      if ([TAG_ITEM, FGA_POLICY_TYPE_PREFIX, GALAXI_TYPE, DATAZONE_TYPE].includes(props.selectedPermissionType)) {
        return props.allPermissionDropdownOptions?.filter((dropdownOption) => {
          return dropdownOption.type == props.selectedPermissionType;
        });
      } else {
        return props.allPermissionDropdownOptions?.filter((dropdownOption) => {
          return dropdownOption.type == RESOURCE_ITEM;
        });
      }
    }
  };

  useEffect(() => {
    getDropDownItemsBasedOnSelectionType();
    setSelectedPermission(undefined);
    setSelectedPermissions(undefined);
    setItems([]);
  }, [props.selectedPermissionType]);

  const getPermissionTypeDescription = (selectedPermissionType) => {
    switch (selectedPermissionType) {
      case RESOURCE_ITEM:
        return 'Select resource access permission provided by the data provider';
      case TAG_ITEM:
        return 'Select tag access permission defined by the data provider';
      case FGA_POLICY_TYPE_PREFIX:
        return 'Select fine grain access policies permission defined by the data provider';
      default:
        return 'Select data permission defined by the data provider';
    }
  };

  const getGalaxiGroupForm = () => {
    let alertMsg = `You will be redirected outside of ${WEBSITE_NAME} to continue your subscription.`;
    if (!onboardedToGalaxi) {
      alertMsg = 'To subscribe to this dataset, you must first onboard on the Galaxi platform.';
    }
    return (
      (onboardedToGalaxi && (
        <FormField
          label='Target galaxi group'
          description='Select the target galaxi group for your subscription request'
        >
          <SpaceBetween size='m'>
            <Select
              selectedOption={selectedPermission}
              onChange={handleChange}
              options={getDropDownItemsBasedOnSelectionType()}
              selectedAriaLabel='Selected'
              empty={
                <EmptyState
                  title='No galaxi groups found'
                  subtitle='To subscribe to this dataset, you must first onboard on the Galaxi platform.'
                />
              }
              statusType={loadingResources ? 'loading' : 'finished'}
              loadingText='Loading access options'
            />
            <Alert statusIconAriaLabel='Warning' type='warning'>
              {alertMsg}
            </Alert>
          </SpaceBetween>
        </FormField>
      )) || (
        <Alert statusIconAriaLabel='Warning' type='warning'>
          {alertMsg}
        </Alert>
      )
    );
  };

  return (
    <Modal
      onDismiss={props.dismiss}
      visible={props.visible}
      closeAriaLabel='Close modal'
      footer={
        <Box float='right'>
          <SpaceBetween direction='horizontal' size='xs'>
            <Button variant='link' onClick={props.dismiss}>
              Cancel
            </Button>
            <Button variant='primary' onClick={handleConfirm}>
              {buttonText}
            </Button>
          </SpaceBetween>
        </Box>
      }
      size='medium'
      header={'Subscribe to dataset'}
    >
      <ColumnLayout>
        {props.selectedPermissionType && !isGalaxiPermission && (
          <FormField
            label='Select permission(s)'
            description={getPermissionTypeDescription(props.selectedPermissionType)}
          >
            {props.selectedPermissionType != FGA_POLICY_TYPE_PREFIX && (
              <Select
                selectedOption={selectedPermission}
                onChange={handleChange}
                options={getDropDownItemsBasedOnSelectionType()}
                selectedAriaLabel='Selected'
                empty='No access options'
                statusType={loadingResources ? 'loading' : 'finished'}
                loadingText='Loading access options'
                data-testid={'select-permissions'}
              />
            )}
            {props.selectedPermissionType == FGA_POLICY_TYPE_PREFIX && (
              <Multiselect
                options={getDropDownItemsBasedOnSelectionType()}
                selectedAriaLabel='Selected'
                selectedOptions={selectedPermissions}
                onChange={handleChange}
                empty='No access options'
                statusType={loadingResources ? 'loading' : 'finished'}
                loadingText='Loading access options'
              />
            )}
          </FormField>
        )}
        {props.selectedPermissionType && isGalaxiPermission && (
          <>
            {!groupServiceSupported && (
              <FormField>
                <Alert statusIconAriaLabel='Warning' type='warning'>
                  You will be redirected outside of {WEBSITE_NAME} to continue your subscription.
                </Alert>
              </FormField>
            )}
            {groupServiceSupported && getGalaxiGroupForm()}
          </>
        )}

        {(selectedPermission || selectedPermissions) &&
          props.selectedPermissionType &&
          !isGalaxiPermission &&
          groupServiceSupported &&
          isDWaaSGroup(props.activeGroup) && <>{getGalaxiGroupDropdown()}</>}

        {(selectedPermission !== undefined || selectedPermissions !== undefined) &&
          !isRedshift &&
          !isGalaxiPermission &&
          !isDWaaSGroup(props.activeGroup) && (
            <FormField label='Select Lake Formation access principal type'>
              <Select
                selectedOption={selectedPrincipalType}
                onChange={(e) => {
                  setSelectedPrincipalType(e.detail.selectedOption);
                }}
                options={dropdownPrincipalOptions}
                statusType={loadingResources ? 'loading' : 'finished'}
                loadingText='Loading access options'
                data-testid={'select-permissions-principal-type'}
              />
            </FormField>
          )}
        {(selectedPermission !== undefined || selectedPermissions !== undefined) && isDataZone && (
          <FormField label='Consumer project' description={'Select which DataZone project to request for.'}>
            <Select
              selectedOption={selectedDzProject}
              options={dzProjectOptions}
              onChange={(e) => setSelectedDzProject(e.detail.selectedOption)}
              statusType={loadingResources ? 'loading' : 'finished'}
              loadingText='Loading projects'
            />
          </FormField>
        )}
        {(selectedPermission !== undefined || selectedPermissions !== undefined) &&
          selectedPrincipalType !== undefined &&
          !isGalaxiPermission &&
          selectedPrincipalType.value === 'iamRoleUserAccess' && (
            <FormField
              label='IAM User/Role'
              description='Enter the ARN for the IAM user or role'
              errorText={principalErrorText}
            >
              <Input
                ariaRequired={true}
                placeholder={'arn:aws:iam::123456789012:role/AWSDGSFoundry-ManagementRole-DO-NOT-DELETE'}
                value={principal}
                onChange={(e) => {
                  setPrincipalErrorText(undefined);
                  setPrincipal(e.detail.value);
                }}
                disabled={selectedGalaxiGroup != null}
              />
            </FormField>
          )}
        {(selectedPermission !== undefined || selectedPermissions !== undefined) &&
          !isGalaxiPermission &&
          !isDataZone &&
          displayResource()}
      </ColumnLayout>
    </Modal>
  );
};
